import { useEffect, useState } from 'react';
import { ApiError, getDashboard, type DashboardData } from '../lib/api.js';
import { useFlywheel } from '../lib/flywheel.js';

/**
 * Operator dashboard. Implements use case O1 from
 * docs/use_cases_v0.1.md §4.4.
 *
 * Three panels:
 *   1. Topic distribution — what worries / interests are showing up
 *   2. Senpai popularity — which personas are getting matched
 *   3. Tuning hint — gpt-5-mini-generated single-paragraph suggestion in
 *      question form (per Q15 in open_questions.md)
 *
 * The tuning hint is regenerated on every page load; topics are a curated
 * baseline; popularity is baseline + live session counts. The data_note
 * footer makes that distinction visible.
 */
export function Operator() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { emit } = useFlywheel();

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setData(null);
    getDashboard()
      .then((d) => {
        if (cancelled) return;
        setData(d);
        // Operator pulled aggregate signal → loop closes back to alumni
        // (program tuning insight feeds into how the next cohort writes).
        emit({
          kind: 'DASHBOARD_REFRESHED',
          actor: 'operator',
          flowsTo: 'alumni',
          note: '今週の声を集計',
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.userMessage : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [emit]);

  return (
    <div className="main">
      <div className="container container-wide">
        <h1>運営ダッシュボード</h1>
        <p className="lede">今週、候補者の声と先輩との対話で見えてきたもの。</p>

        {error && <div className="error">取得に失敗しました: {error}</div>}

        {!data && !error && (
          <div className="muted center loading-block">集計しています...</div>
        )}

        {data && (
          <>
            <div className="callout">
              今週、新たに先輩のログ・振り返りが <strong>{data.new_alumni_logs}件</strong> 追加されました。
            </div>

            {data.weekly && (
              <Section title="今週のアクティビティ">
                <div className="weekly-grid">
                  <WeeklyStat label="新規ログ" value={data.weekly.new_logs} />
                  <WeeklyStat
                    label="プラン項目"
                    value={data.weekly.new_plans}
                    hint="出発前ユーザーの計画"
                  />
                  <WeeklyStat
                    label="帰国後の振り返り"
                    value={data.weekly.new_reflections}
                  />
                  <WeeklyStat
                    label="進行中の対話"
                    value={data.weekly.active_sessions}
                    hint="候補者と先輩のセッション"
                  />
                </div>
              </Section>
            )}

            <Section title="関心トピック">
              <TopicBars topics={data.topics} />
            </Section>

            <Section title="先輩の人気">
              <PopularityList items={data.senpai_popularity} />
            </Section>

            <Section title="プログラム調整のヒント">
              <blockquote className="quote">
                {data.tuning_hint || <span className="muted">(生成できませんでした)</span>}
              </blockquote>
              <p className="footnote">※ ページ読み込みごとに gpt-5-mini が再生成します。</p>
            </Section>

            <p className="footnote footnote-trailing">
              {data.data_note} 集計時刻 {new Date(data.generated_at).toLocaleString('ja-JP')}.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function WeeklyStat({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div className="weekly-stat">
      <div className="weekly-stat-value">{value}</div>
      <div className="weekly-stat-label">{label}</div>
      {hint && <div className="weekly-stat-hint">{hint}</div>}
    </div>
  );
}

function TopicBars({ topics }: { topics: { tag: string; count: number }[] }) {
  if (topics.length === 0) return <div className="muted">データがありません。</div>;
  const max = Math.max(...topics.map((t) => t.count));
  return (
    <div className="panel">
      {topics.map((t) => (
        <div key={t.tag} className="topic-row">
          <div className="topic-label">{t.tag}</div>
          <div className="topic-track">
            <div className="topic-fill" style={{ width: `${(t.count / max) * 100}%` }} />
          </div>
          <div className="topic-count">{t.count}</div>
        </div>
      ))}
    </div>
  );
}

function PopularityList({
  items,
}: {
  items: { agent_id: string; display_name: string; count: number }[];
}) {
  if (items.length === 0) return <div className="muted">データがありません。</div>;
  return (
    <div className="popularity-grid">
      {items.map((s) => (
        <div key={s.agent_id} className="popularity-card">
          <div className="avatar avatar-sm">{s.display_name.slice(0, 1)}</div>
          <div className="popularity-meta">
            <div className="popularity-name">{s.display_name}</div>
            <div className="popularity-count">対話 {s.count} 件</div>
          </div>
        </div>
      ))}
    </div>
  );
}
